import { DndContext, closestCenter, useSensor, useSensors, PointerSensor, KeyboardSensor } from '@dnd-kit/core';
import { useSortable, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToParentElement } from '@dnd-kit/modifiers';

import { RegistrationQuestion, CreateRegistrationQuestion, QuestionType } from '../../../../../../types/working-model';
import PillToggle from '../../../../../general-ui/pill-toggle/pill-toggle';
import Switch from '../../../../../general-ui/switch/switch';
import TextInput from '../../../../../general-ui/text-input/text';
import SelectInput from '../../../../../general-ui/select/select';
import Icon, { COLORS, ICONS } from '../../../../../general-ui/icon';


const QuestionOptions = [
	{ label: "Text field", value: QuestionType.text },
	{ label: "Text area", value: QuestionType.textarea },
	{ label: "Dropdown list", value: QuestionType.select },
	{ label: "Checkbox", value: QuestionType.checkbox },
	{ label: "Numeric Field", value: QuestionType.numeric },
	{ label: "Date Field", value: QuestionType.date },
	{ label: "Email Field", value: QuestionType.email }
];


const RegistrationOption = (props: any) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
	} = useSortable({ id: props.id });

	const { option, index, removeOption, editOption } = props;

	const style = {
		transform: CSS.Transform.toString(transform),
		transition: 'none',
	};

	return (
		<div ref={setNodeRef} className="edit-registration-question-option" style={style}>
			<div className="drag-handle" {...attributes} {...listeners}>
				<Icon name={ICONS.DRAG_HANDLE} size={18} color={COLORS.DEFAULT_GRAY} />
			</div>
			<TextInput
				defaultValue={option}
				onChange={() => ({})}
				onBlur={editOption}
				placeholder="Enter field name"
			/>
			<button onClick={() => removeOption(index)} className="round"><Icon name={ICONS.TRASH} size={10} color={COLORS.WHITE} /></button>
		</div>
	);
};

interface IEditCustomQuestionProps {
	editingQuestion: RegistrationQuestion | CreateRegistrationQuestion;
	setEditingQuestion: (question: RegistrationQuestion | CreateRegistrationQuestion) => void;
	requiredQuestions: number[];
	setQuestionRequired: (question: number, value: number) => void;
}

const EditCustomQuestion: React.FC<IEditCustomQuestionProps> = ({ editingQuestion, setEditingQuestion, requiredQuestions, setQuestionRequired }) => {

	const updateQuestionName = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>): void => {
		if (!editingQuestion) return;
		setEditingQuestion({ ...editingQuestion, name: value });
	};

	const changeQuestionType = (value: QuestionType): void => {
		if (!editingQuestion) return;
		setEditingQuestion({ ...editingQuestion, type: value as QuestionType });
	};

	const toggleDefaultChecked = (value: any, isOn: boolean) => {
		if (!editingQuestion) return;
		setEditingQuestion({ ...editingQuestion, default_value: isOn ? "checked" : "" });
	};

	const addOption = () => {
		if (!editingQuestion) return;
		setEditingQuestion({ ...editingQuestion, options: [...editingQuestion.options ?? [], 'New Option'] });
	};

	const removeOption = (index: number) => {
		if (!editingQuestion) return;
		const _editingQuestion = { ...editingQuestion };
		_editingQuestion!.options = _editingQuestion.options!.filter((option: string, i: number) => i !== index);
		setEditingQuestion(_editingQuestion);
	};

	const editOption = (index: number) => {
		return ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
			const updateOptions = editingQuestion?.options ?? [];
			updateOptions[index] = value;
			if (!editingQuestion) return;
			setEditingQuestion({ ...editingQuestion, options: updateOptions });
		};
	};

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const handleDragEnd = (event: any) => {
		if (!editingQuestion) return;
		const _modules = [...editingQuestion.options!];
		const startIndex = _modules.findIndex((option: string) => option === event.active.id);
		const newIndex = _modules.findIndex((option: string) => option === event.over.id);
		const [option] = _modules.splice(startIndex, 1);
		_modules.splice(newIndex, 0, option);
		setEditingQuestion({ ...editingQuestion, options: _modules });
	};

	return (
		<div style={{ position: 'relative' }}>
			<div className="edit-registration-fields">
				<TextInput
					id={"registration_question" in editingQuestion ? String(editingQuestion.registration_question) : "0"}
					defaultValue={editingQuestion.name}
					placeholder={"Enter field name"}
					onChange={() => ({})}
					onBlur={updateQuestionName}
				/>
				<div className="field-group select">
					<SelectInput
						options={QuestionOptions}
						selected={editingQuestion.type}
						onChange={changeQuestionType}
						ignorePositioning
						ignoreSpacing
						ignoreInlineStyling
						small
					/>
				</div>

				<PillToggle
					selected={"registration_question" in editingQuestion && requiredQuestions.includes(editingQuestion.registration_question) ? 1 : 0}
					value={"registration_question" in editingQuestion ? editingQuestion.registration_question : 0}
					disabled={"registration_question" in editingQuestion ? false : true}
					onClick={setQuestionRequired}
				/>
			</div>

			{editingQuestion.type === QuestionType.checkbox && (
				<div className="field-group switch">
					<Switch
						on={editingQuestion.default_value === 'checked'}
						value={"registration_question" in editingQuestion ? editingQuestion.registration_question : 0}
						onClick={toggleDefaultChecked}
					/>
					<label>Checked by default</label>
				</div>

			)}

			{editingQuestion.type === QuestionType.select && (
				<>
					<div className="edit-registration-question-options">
						<DndContext
							sensors={sensors}
							collisionDetection={closestCenter}
							onDragEnd={handleDragEnd}
							modifiers={[restrictToParentElement]}
							autoScroll={false}
						>
							<SortableContext
								items={editingQuestion.options ?? []}
								strategy={verticalListSortingStrategy}
							>
								{editingQuestion.options?.map((option: string, index: number) => (
									<RegistrationOption
										option={option}
										index={index}
										key={option}
										editOption={editOption(index)}
										removeOption={removeOption}
										id={option}
									/>
								))}
							</SortableContext>
						</DndContext>
					</div>
					<button className="small" onClick={addOption}><Icon name={ICONS.ADD} size={12} color={COLORS.WHITE} /><span>Add Option</span></button>
				</>
			)}
		</div>
	);
};

export default EditCustomQuestion;